import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Form, useFormik } from 'formik';
import { useGoogleLogin } from '@react-oauth/google';
import { LoginViewPropsType } from './types';
import {
	loginUserWithEmailPasswordAction,
	socialLoginUserAction,
} from '@Redux/store/user/actions';
import { StoreType } from '@Redux/store/types';
import { useIntl } from 'react-intl';
import CustomIcon from '@Assets/images/icons';
import { useState } from 'react';
import { loginSchema } from '../../containers/loginViewContainer/validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamation } from '@fortawesome/free-solid-svg-icons';
import { ContainerIcon } from '@Components/Password/styled';
import { Header, Text } from '@Components/Text';
import {
	ButtonGoogle,
	ContainerBtnLogin,
	ContainerGoogle,
	HeaderLink,
	HeaderText,
	StyledLink,
	TextButtonGoogle,
	TextLogin,
	TitleLogin,
} from './styled';
import { Button } from '@Components/Buttons';
import GoogleLogo from '@Assets/images/logos/GoogleLogo';
import { getClientFromCurrentUrl } from '@Utils/functions';

const initialValues = {
	email: '',
	password: '',
};

const LoginLayout: React.FC<LoginViewPropsType> = () => {
	const clientFromUrl = getClientFromCurrentUrl();
	const dispatch = useDispatch();
	const intl = useIntl();
	const {
		login: { loading, error },
	} = useSelector((state: StoreType) => state.user);

	const formik = useFormik({
		initialValues,
		validationSchema: loginSchema,
		onSubmit: ({ email, password }) => {
			dispatch(loginUserWithEmailPasswordAction(email.toLowerCase(), password));
		},
	});
	const googleLogin = useGoogleLogin({
		onSuccess: (response) => {
			const { code } = response;
			dispatch(socialLoginUserAction(code));
		},
		flow: 'auth-code',
	});

	const [showPassword, setShowPassword] = useState(false);
	const switchShowPassword = () => setShowPassword(!showPassword);

	const handleSubmit = () => {
		formik.handleSubmit();
	};

	return (
		<div className="form w-100 ">
			{/* begin::Heading */}
			<div className="text-center mb-10 ">
				<TitleLogin>{intl.formatMessage({ id: 'AUTH.WELCOME1' })}</TitleLogin>
				<Header
					variant="h5"
					weight="bold"
					style={{
						color: '#5E6278',
					}}
				>
					{intl.formatMessage({ id: 'AUTH.INITIAL' })}
				</Header>
			</div>

			<div className="fv-row mb-10">
				<label>
					<Text className="mb-2" weight="bold">
						{intl.formatMessage({ id: 'AUTH.EMAIL' })}{' '}
					</Text>
				</label>
				<input
					placeholder={intl.formatMessage({ id: 'AUTH.PLACEHOLDER.EMAIL' })}
					{...formik.getFieldProps('email')}
					className={clsx(
						'form-control form-control-lg form-control-solid',
						{ 'is-invalid': formik.touched.email && formik.errors.email },
						{
							'is-valid': formik.touched.email && !formik.errors.email,
						}
					)}
					type="email"
					name="email"
					autoComplete="off"
				/>
				{formik.touched.email && formik.errors.email && (
					<div className="fv-plugins-message-container text-danger mt-2">
						<span role="alert">
							{intl.formatMessage({ id: formik.errors.email })}
						</span>
					</div>
				)}
			</div>

			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<div className=" mb-10 ">
					<div className="d-flex justify-content-between mt-n5 ">
						<div className="d-flex justify-content-between align-content-between mb-2 w-100">
							<div>
								<label>
									<Text className="mb-0" weight="bold">
										{intl.formatMessage({ id: 'AUTH.PASSWORD' })}{' '}
									</Text>
								</label>
							</div>
							<div>
								<Link to="/auth/forgot-password">
									<StyledLink>
										{intl.formatMessage({ id: 'AUTH.FORGOT.TITLE' })}
									</StyledLink>
								</Link>
							</div>
						</div>
					</div>
					<div className="position-relative mb-3 ">
						<input
							type={showPassword ? 'text' : 'password'}
							placeholder={intl.formatMessage({
								id: 'AUTH.PLACEHOLDER.PASSWORD',
							})}
							autoComplete="off"
							{...formik.getFieldProps('password')}
							className={clsx(
								'form-control form-control-lg form-control-solid pr-100px '
							)}
						/>
						{formik.touched.password && !formik.errors.password && (
							<FontAwesomeIcon
								icon={faCheck}
								color="#50CD89"
								style={{
									height: '1.929rem',
									width: '1.929rem',
									marginRight: '0.357rem',
									marginTop: '-0.143rem',
								}}
								className={clsx(`position-absolute top-0 end-0 pt-4 pe-12  `)}
							/>
						)}
						{formik.touched.password && formik.errors.password && (
							<ContainerIcon>
								<FontAwesomeIcon
									icon={faExclamation}
									color="#f1416c"
									style={{
										height: '1.071rem',
										width: '1.214rem',
										display: 'flex',
										justifyContent: 'center',
									}}
								/>
							</ContainerIcon>
						)}
						<CustomIcon
							icon={showPassword ? 'eyeOpen' : 'eyePassword'}
							onClick={() => setShowPassword(!showPassword)}
							className={clsx(
								`position-absolute top-0 end-0 pt-4 pe-5  bg-transparent btn`
							)}
						/>
					</div>
					{formik.touched.password && formik.errors.password && (
						<div className="fv-plugins-message-container text-danger">
							<div className="fv-help-block">
								<span role="alert">
									{intl.formatMessage({ id: formik.errors.password })}
								</span>
							</div>
						</div>
					)}
				</div>
			</form>

			{error && (
				<div className="alert alert-danger mt-2">
					{intl.formatMessage({ id: error.code.toString() })}
				</div>
			)}
			{clientFromUrl === 1 && (
				<div className="d-flex justify-content-center align-items-center text-center text-gray-400 fw-bold fs-4 mb-4 w-100">
					<HeaderText>
						{intl.formatMessage({ id: 'AUTH.NOT.ACCOUNT' })}{' '}
					</HeaderText>
					<Link to="/auth/register">
						<HeaderLink>
							{intl.formatMessage({ id: 'AUTH.CREATE.ACCOUNT' })}
						</HeaderLink>
					</Link>
				</div>
			)}
			{/* begin::Action */}
			<div className="text-center">
				<button
					onClick={handleSubmit}
					className="btn btn-lg btn-primary w-100 mb-5"
					disabled={!formik.isValid}
				>
					{!loading && (
						<span className="indicator-label">
							{' '}
							<TextLogin>
								{intl.formatMessage({ id: 'AUTH.SIGN.IN' })}
							</TextLogin>
						</span>
					)}
					{loading && (
						<ContainerBtnLogin>
							<TextLogin>
								{intl.formatMessage({ id: 'AUTH.SIGN.LOADER' })}
							</TextLogin>
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</ContainerBtnLogin>
					)}
				</button>
				{/* begin::Separator */}
				{/* <div className="text-center text-muted fw-bolder mb-5">
					{intl.formatMessage({ id: 'AUTH.GENERAL.OR' })}{' '}
				</div> */}
				{/* end::Separator */}
				{clientFromUrl === 1 && (
					<ContainerGoogle>
						<ButtonGoogle onClick={googleLogin} variant="light">
							<GoogleLogo />
							<TextButtonGoogle>
								{intl.formatMessage({ id: 'AUTH.INGRESS.GOOGLE' })}
							</TextButtonGoogle>
						</ButtonGoogle>
					</ContainerGoogle>
				)}

				{/* <a
					href="#"
					className="btn btn-flex flex-center btn-light btn-lg w-100 mb-5"
				>
					<img
						alt="Logo"
						src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
						className="h-20px me-3"
					/>
					{intl.formatMessage({ id: 'AUTH.INGRESS.GOOGLE' })}
				</a>

				<a
					href="#"
					className="btn btn-flex flex-center btn-light btn-lg w-100 mb-5"
				>
					<img
						alt="Logo"
						src={toAbsoluteUrl('/media/svg/brand-logos/facebook-4.svg')}
						className="h-20px me-3"
					/>
					{intl.formatMessage({ id: 'AUTH.INGRESS.FACEBOOK' })}
				</a> */}
			</div>
		</div>
	);
};

export default LoginLayout;
